import { useEffect, useState } from "react";
import { useLocation, useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, Eye, EyeOff, KeyRound, ShieldCheck, AlertTriangle } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/** Strength meter — simple length + variety heuristic. We intentionally
 * AVOID the zxcvbn library here so the bundle stays small; this gives a
 * "good-enough" signal to nudge the user toward a strong password without
 * pretending to be a security oracle. */
function score(pw) {
    if (!pw) return { label: "—", pct: 0, color: "bg-slate-200" };
    let n = 0;
    if (pw.length >= 8) n += 1;
    if (pw.length >= 12) n += 1;
    if (/[A-Z]/.test(pw)) n += 1;
    if (/[a-z]/.test(pw)) n += 1;
    if (/[0-9]/.test(pw)) n += 1;
    if (/[^A-Za-z0-9]/.test(pw)) n += 1;
    const labels = ["very weak", "weak", "ok", "fair", "good", "strong", "excellent"];
    const colors = [
        "bg-rose-400", "bg-rose-400", "bg-amber-400",
        "bg-amber-400", "bg-emerald-400", "bg-emerald-500", "bg-emerald-600",
    ];
    return { label: labels[n], pct: ((n + 1) / 7) * 100, color: colors[n] };
}

/** Landing page for the single-use "set your password" link emailed by an
 * admin or self-service reset flow. Walks the user through three states:
 *
 *   1. Loading — verify the token (404/410 → friendly bounce screen)
 *   2. Form — type + confirm new password (visible strength meter)
 *   3. Success — auto-redirect to /login with a toast
 *
 * The token NEVER appears in the rendered DOM; we read it from the URL
 * once and keep it in state. Browser history is replaced so refreshing
 * the success state doesn't try to redeem the consumed token.
 */
export default function SetPassword() {
    const loc = useLocation();
    const nav = useNavigate();
    const params = new URLSearchParams(loc.search);
    const token = params.get("token") || "";

    const [stage, setStage] = useState("checking");  // checking | form | done | invalid
    const [email, setEmail] = useState("");
    const [errMsg, setErrMsg] = useState("");
    const [pw, setPw] = useState("");
    const [pw2, setPw2] = useState("");
    const [show, setShow] = useState(false);
    const [busy, setBusy] = useState(false);

    // Pre-flight: confirm the token is valid the moment the page mounts so
    // we never let the user type out a password on a dead link.
    useEffect(() => {
        if (!token) { setStage("invalid"); setErrMsg("This link is missing its token."); return; }
        let alive = true;
        (async () => {
            try {
                const { data } = await axios.post(`${API}/auth/password-reset/verify-token`, { token });
                if (!alive) return;
                setEmail(data.email);
                setStage("form");
            } catch (e) {
                if (!alive) return;
                setStage("invalid");
                setErrMsg(e?.response?.data?.detail || "Link is invalid or expired");
            }
        })();
        return () => { alive = false; };
    }, [token]);

    const submit = async (e) => {
        e?.preventDefault?.();
        if (pw.length < 8) { toast.error("Password must be at least 8 characters"); return; }
        if (pw !== pw2) { toast.error("Passwords don't match"); return; }
        setBusy(true);
        try {
            await axios.post(`${API}/auth/password-reset/complete`, { token, new_password: pw });
            setStage("done");
            toast.success("Password set — you can now sign in.");
            // Bounce to /login after a short beat so the success card is
            // readable. `replace: true` so the back button doesn't put the
            // user on a stale "set password" URL whose token is now used.
            setTimeout(() => nav("/login", { replace: true }), 1400);
        } catch (e2) {
            toast.error(e2?.response?.data?.detail || "Couldn't set password");
        } finally {
            setBusy(false);
        }
    };

    const s = score(pw);

    return (
        <div className="min-h-screen w-full bg-gradient-to-b from-slate-50 to-white flex items-center justify-center px-4" data-testid="set-password-page">
            <div className="w-full max-w-md">
                <div className="flex items-center justify-center mb-6">
                    <Link to="/" className="font-display text-2xl tracking-tight text-slate-900">toothrocket<sup className="text-cyan-600 text-xs ms-0.5">™</sup></Link>
                </div>

                <div className="bg-white rounded-2xl shadow-xl shadow-slate-200/50 border border-slate-100 p-8">
                    {stage === "checking" && (
                        <div className="flex flex-col items-center py-10" data-testid="set-password-checking">
                            <Loader2 size={28} className="animate-spin text-cyan-600" />
                            <div className="mt-4 text-[13px] text-slate-500 font-mono-tr tracking-[0.18em] uppercase">Verifying link…</div>
                        </div>
                    )}

                    {stage === "invalid" && (
                        <div className="text-center py-6" data-testid="set-password-invalid">
                            <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-rose-50 text-rose-600 mb-3">
                                <AlertTriangle size={20} />
                            </div>
                            <div className="font-mono-tr text-[11px] tracking-[0.22em] uppercase text-rose-700 mb-2">· Link unavailable</div>
                            <h1 className="font-display text-2xl text-slate-900 tracking-tight">This link can&rsquo;t be used.</h1>
                            <p className="mt-2 text-[13.5px] text-slate-600 leading-relaxed">{errMsg}</p>
                            <p className="mt-4 text-[12.5px] text-slate-500">
                                Ask your administrator to send a fresh reset link, or
                                {" "}<Link to="/login" className="text-cyan-700 underline">return to sign-in</Link>.
                            </p>
                        </div>
                    )}

                    {stage === "form" && (
                        <form onSubmit={submit} data-testid="set-password-form">
                            <div className="font-mono-tr text-[11px] tracking-[0.22em] uppercase text-cyan-700 mb-2">· Set your password</div>
                            <h1 className="font-display text-2xl text-slate-900 tracking-tight">Choose a new password</h1>
                            <p className="mt-1 text-[13px] text-slate-500">Setting password for <span className="font-medium text-slate-800" data-testid="set-password-email">{email}</span></p>

                            <div className="mt-6 space-y-3.5">
                                <label className="block">
                                    <div className="font-mono-tr text-[10.5px] tracking-[0.22em] uppercase text-slate-500 mb-1.5">New password</div>
                                    <div className="relative">
                                        <input
                                            type={show ? "text" : "password"}
                                            value={pw}
                                            onChange={(e) => setPw(e.target.value)}
                                            disabled={busy}
                                            autoFocus
                                            required
                                            minLength={8}
                                            data-testid="set-password-new"
                                            placeholder="At least 8 characters"
                                            className="w-full px-3 py-2.5 pe-10 rounded-lg border border-slate-200 focus:border-cyan-500 focus:ring-2 focus:ring-cyan-100 outline-none text-[14px]"
                                        />
                                        <button
                                            type="button"
                                            onClick={() => setShow((v) => !v)}
                                            tabIndex={-1}
                                            className="absolute end-2.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700"
                                            aria-label={show ? "Hide password" : "Show password"}
                                        >
                                            {show ? <EyeOff size={16} /> : <Eye size={16} />}
                                        </button>
                                    </div>
                                    {pw && (
                                        <div className="mt-1.5">
                                            <div className="h-1 bg-slate-100 rounded-full overflow-hidden">
                                                <div className={`h-full ${s.color} transition-all`} style={{ width: `${s.pct}%` }} />
                                            </div>
                                            <div className="mt-1 font-mono-tr text-[10.5px] tracking-wide uppercase text-slate-500" data-testid="set-password-strength">strength · {s.label}</div>
                                        </div>
                                    )}
                                </label>

                                <label className="block">
                                    <div className="font-mono-tr text-[10.5px] tracking-[0.22em] uppercase text-slate-500 mb-1.5">Confirm password</div>
                                    <input
                                        type={show ? "text" : "password"}
                                        value={pw2}
                                        onChange={(e) => setPw2(e.target.value)}
                                        disabled={busy}
                                        required
                                        minLength={8}
                                        data-testid="set-password-confirm"
                                        placeholder="Type it again"
                                        className="w-full px-3 py-2.5 rounded-lg border border-slate-200 focus:border-cyan-500 focus:ring-2 focus:ring-cyan-100 outline-none text-[14px]"
                                    />
                                </label>
                            </div>

                            <button
                                type="submit"
                                disabled={busy || !pw || pw !== pw2}
                                data-testid="set-password-submit"
                                className="w-full mt-6 px-4 py-3 rounded-lg bg-[#0B1120] hover:bg-slate-800 disabled:opacity-50 text-white text-[14px] font-medium inline-flex items-center justify-center gap-2 transition-colors"
                            >
                                {busy ? <Loader2 size={15} className="animate-spin" /> : <KeyRound size={14} />}
                                {busy ? "Setting password…" : "Set password & sign in"}
                            </button>

                            <p className="mt-4 text-center text-[11.5px] text-slate-400 leading-relaxed">
                                This link is single-use and expires soon. Don&rsquo;t share it.
                            </p>
                        </form>
                    )}

                    {stage === "done" && (
                        <div className="text-center py-6" data-testid="set-password-done">
                            <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-emerald-50 text-emerald-600 mb-3">
                                <ShieldCheck size={22} />
                            </div>
                            <div className="font-mono-tr text-[11px] tracking-[0.22em] uppercase text-emerald-700 mb-2">· Done</div>
                            <h1 className="font-display text-2xl text-slate-900 tracking-tight">Password set.</h1>
                            <p className="mt-2 text-[13.5px] text-slate-600">Redirecting you to sign in…</p>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}
